import React, { useMemo } from "react";
import { ChevronRight, LogOut } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { cn } from "../lib/cn";
import SidebarNav from "./navigation/SidebarNav";
import { getNavigationGroups } from "./navigation/navigationConfig";

interface SidebarProps {
  onToggle: () => void;
  isExpanded: boolean;
}

const Sidebar = ({ onToggle, isExpanded }: SidebarProps) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigationGroups = useMemo(() => getNavigationGroups(user?.role), [user?.role]);

  if (!user) {
    return null;
  }

  return (
    <aside
      id="logo-sidebar"
      className={cn(
        "fixed left-4 top-20 z-40 h-[calc(100vh-6rem)] rounded-2xl bg-white/85 pt-3 font-montserrat shadow-lg backdrop-blur-sm transition-all duration-300 dark:bg-gray-900/75",
        isExpanded ? "w-64" : "w-16",
      )}
      aria-label="Sidebar"
    >
      <button
        type="button"
        className={cn(
          "absolute -right-3.5 top-36 flex h-7 w-7 items-center justify-center rounded-md bg-white/90 shadow-sm transition-transform duration-300 hover:bg-white dark:bg-gray-900/90 dark:hover:bg-gray-900",
          isExpanded ? "rotate-180" : "rotate-0",
        )}
        onClick={onToggle}
        title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
      >
        <ChevronRight className="h-4 w-4 text-gray-700 transition-colors duration-200 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white" />
      </button>

      <div className="flex h-full flex-col gap-4 px-3 pb-4">
        <SidebarNav
          currentPath={location.pathname}
          isExpanded={isExpanded}
          items={navigationGroups.primary}
        />

        {navigationGroups.secondary.length > 0 ? (
          <div className="mt-auto border-t border-orange-100 pt-4 dark:border-gray-800">
            <SidebarNav
              currentPath={location.pathname}
              isExpanded={isExpanded}
              items={navigationGroups.secondary}
            />

            <button
              type="button"
              onClick={logout}
              className={cn(
                "mt-2 flex w-full items-center rounded-xl p-2.5 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-gray-300 dark:hover:bg-red-500/10 dark:hover:text-red-400",
                isExpanded ? "justify-start" : "justify-center",
              )}
            >
              <LogOut className="h-5 w-5 shrink-0" />
              {isExpanded ? <span className="ml-3">Logout</span> : null}
            </button>
          </div>
        ) : null}
      </div>
    </aside>
  );
};

export default Sidebar;
